import { SectionEnum } from 'gql/graphql';
import type { IListItem } from 'types/Contract';
import { cnMixSpace, Combobox, Grid, GridItem } from 'uikit';

import type { ForwardedRef } from 'react';
import { useCallback, forwardRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getActualBimWorkOrderChaptersAndSectionsListItems, totalsByConstructionTypeKey } from '../types';

interface IBimWorkOrderFilter {
    rootObject?: IListItem<number>;
    sectionOrChapter?: IListItem<string>;
    onRootObjectChange: (rootObject: IListItem<number> | undefined) => void;
    onSectionOrChapterChange: (sectionOrChapter: IListItem<string>) => void;
    children: any;
    objectsListItems: any;
    constructionTypeId?: number;
    isFrameContract: boolean;
    isWorkOrderChange?: boolean;
}

export const BimWorkOrderFilterWithTools = forwardRef(
    (props: IBimWorkOrderFilter, ref: ForwardedRef<HTMLDivElement>) => {
        const {
            rootObject,
            sectionOrChapter,
            onSectionOrChapterChange,
            onRootObjectChange,
            children,
            objectsListItems,
            constructionTypeId,
            isFrameContract,
            isWorkOrderChange = false,
        } = props;

        const { versionId } = useParams();
        const isVersionPage = !!versionId;

        const isTotalsChapter = useMemo(
            () =>
                sectionOrChapter?.id === totalsByConstructionTypeKey ||
                sectionOrChapter?.id === SectionEnum.WorkOrderTotals,
            [sectionOrChapter?.id],
        );

        const onCurrentObjectChange = useCallback(
            ({ value }) => {
                onRootObjectChange(value);
            },
            [onRootObjectChange],
        );

        const onCurrentChapterOrSectionChange = useCallback(
            ({ value }) => {
                onSectionOrChapterChange(value);
            },
            [onSectionOrChapterChange],
        );

        const objectsItems = useMemo(() => {
            const baseItems = ({
                id,
                name,
                workOrderObjectId,
                workOrderChangeObjectId,
            }: {
                id: number;
                name: string;
                workOrderObjectId: number;
                workOrderChangeObjectId?: number;
            }) => ({
                id,
                workOrderObjectId,
                ...(workOrderChangeObjectId ? { workOrderChangeObjectId } : undefined),
                label: name,
            });

            const versionItems = ({
                id,
                name,
                workOrderVersionId,
            }: {
                id: number;
                name: string;
                workOrderVersionId: number;
            }) => ({
                id,
                workOrderVersionId,
                label: name,
            });

            const res = isVersionPage ? versionItems : baseItems;

            return (objectsListItems ?? [])
                .filter(
                    ({ constructionType, parentId }: { constructionType: { id: number }; parentId: number }) =>
                        parentId === null && constructionTypeId === constructionType.id,
                )
                .map(res)
                .sort(({ id: idA }: { id: number }, { id: idB }: { id: number }) => idA - idB);
        }, [constructionTypeId, isVersionPage, objectsListItems]);

        const bimWorkOrderChaptersAndSectionsListItems = useMemo(
            () => getActualBimWorkOrderChaptersAndSectionsListItems(isFrameContract, isWorkOrderChange),
            [isFrameContract, isWorkOrderChange],
        );

        return (
            <Grid
                cols={12}
                gap="xl"
                rowGap="xl"
                colGap="xl"
                className={cnMixSpace({ mT: 'm', mB: 'xs' })}
                style={{ overflow: 'auto' }}
                ref={ref}
            >
                <GridItem col={2}>
                    <Combobox
                        placeholder="Раздел"
                        items={bimWorkOrderChaptersAndSectionsListItems}
                        value={sectionOrChapter}
                        onChange={onCurrentChapterOrSectionChange}
                        size="s"
                    />
                </GridItem>
                <GridItem col={3}>
                    <Combobox
                        placeholder="Объект"
                        items={objectsItems}
                        value={rootObject}
                        onChange={onCurrentObjectChange}
                        size="s"
                        disabled={isTotalsChapter}
                    />
                </GridItem>
                <GridItem col={6}>{children}</GridItem>
            </Grid>
        );
    },
);

BimWorkOrderFilterWithTools.displayName = 'BimWorkOrderFilter';
